import s from './PageLayout.module.sass'

import { useRouterHandler } from './handlers/useRouterHandler'
import { useUTMhandler } from './handlers/useUTMhandler'
import { useErrorCatcher } from './handlers/useErrorCatcher'
import { useGlobalAnalytic } from './handlers/useGlobalAnalytic'
import { useGlobalData } from './handlers/useGlobalData'
import { useResizeWindow } from './handlers/useResizeWindow'
import { useQueryABTests } from './handlers/useQueryABTests'

import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import React from 'react'

import { Mindbox } from '@app/PageLayout/components/Mindbox'
import { RcmScript } from '@app/PageLayout/components/RcmScript'
import { useAppSelector } from '@app/model/store'

import { ModalFeedback } from '@widgets/Common/ModalFeedback'
import { Footer } from '@widgets/Common/Footer'
import { Header } from '@widgets/Common/Header'
import { Map } from '@widgets/Restaurant/Map'

import { ContainerGlobal } from '@shared/ui/Layout/Containers/ContainerGlobal'
import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { TypeGlobalOutput } from '@shared/api/mappingGlobalProps'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { FeatureTogglePanel } from '@features/Services/ui/FeatureTogglePanel/FeatureTogglePanel'

interface PageLayoutProps {
  children?: JSX.Element
  isLoadingPageProps?: boolean
  globalData?: TypeGlobalOutput
  currentPage: string
}

const LazyBooking = dynamic(() => import('@widgets/Restaurant/Booking/').then((module) => module.Booking), {
  loading: () => <></>,
  ssr: false,
})

const LazyGigAssist = dynamic(() => import('@widgets/Common/GigAssist/').then((module) => module.GigAssist), {
  loading: () => <></>,
  ssr: false,
})

const PageLayout = ({ children, globalData = null, currentPage }: PageLayoutProps) => {
  const isStoreLoaded = useAppSelector((state) => state.global.storeLoaded)
  const storeCityCoords = useAppSelector((state) => state.global.cityRead?.coordinates)
  const mapActive = useAppSelector((state) => state.features.map.active)
  const [isLoadingPage] = useRouterHandler()
  const router = useRouter()

  useResizeWindow()

  useGlobalAnalytic()

  useGlobalData({ data: globalData, page: currentPage })

  useErrorCatcher()

  useQueryABTests()

  useUTMhandler()

  const isOwnerPage = currentPage == 'owner_marketing' || currentPage == 'owner_booking'
  const isOwnerRegPage = currentPage === 'owner_landing_form'

  const isShowHeader = router.pathname !== '/[city_slug]/restaurants/restorator' && !mapActive && !isOwnerRegPage
  const isServiceMasterPages = currentPage === 'not_found' || currentPage === 'service_err'
  const isShowMain = isStoreLoaded && !mapActive
  const isShowFooter = isStoreLoaded && !isOwnerPage && !isServiceMasterPages && !mapActive && !isOwnerRegPage

  const contentBodyClassName = isOwnerPage ? '' : s['body-white']

  const { designEnv } = useClientResponsive()
  const responsiveLoaded = designEnv !== 'default'
  const isShowFilterMap = storeCityCoords && isStoreLoaded

  return (
    <ErrorBoundary>
      {/* Scripts Layer */}
      <>
        <Mindbox />

        <RcmScript currentPage={currentPage} />
      </>

      {/* Modals Layer */}
      <>
        <LazyGigAssist />

        <LazyBooking />

        <ModalFeedback />
      </>

      {/* Page Layer */}
      <div className={s['body']}>
        <div className={contentBodyClassName}>
          <ContainerGlobal className={s.container} data-is-owner={isOwnerPage}>
            {responsiveLoaded && (
              <>
                {/* header block */}
                {isShowHeader && <Header />}

                {isShowFilterMap && <Map />}

                {/* main data block */}
                {isShowMain && (
                  <div className={s['wrapper']} data-is-loading={isLoadingPage}>
                    {children}
                  </div>
                )}

                <FeatureTogglePanel />

                {/* footer block */}
                {isShowFooter && <Footer />}
              </>
            )}
          </ContainerGlobal>
        </div>
      </div>
    </ErrorBoundary>
  )
}

export default PageLayout
